import { useMemo, useRef } from 'react';
import classNames from 'classnames';
import {
  Box,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getWaveUnits } from '@surfline/web-common';

import Information from 'components/Information';
import {
  useMaxWidthTablet,
  useMinWidthMobileLarge,
  useMaxWidthDesktop,
} from 'hooks/useMediaQueries';
import type { ConditionRating } from 'types/conditionRating';
import { useMaxWidthLandscapeMobile } from '../hooks/useTableData';

import styles from './TableHeader.module.scss';

interface Props {
  surfUnits: string | undefined;
  hideProbability: boolean;
  pageIndex?: number;
  ratingsHours: Array<ConditionRating>;
  isHeaderSticky: boolean;
  isHistoricView: boolean;
}

const TableHeader: React.FC<Props> = ({
  surfUnits,
  hideProbability,
  ratingsHours,
  pageIndex = 0,
  isHeaderSticky,
  isHistoricView,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMaxWidthTablet();
  const isLargeMobile = useMinWidthMobileLarge();
  const isTablet = useMaxWidthDesktop();
  const isLandscapeMobile = useMaxWidthLandscapeMobile();

  const headerWrapperClasses = useMemo(
    () =>
      classNames({
        [styles.headerTableOuterWrapper]: true,
        [styles.headerTableOuterWrapperSticky]: isHeaderSticky,
        'sl-section-container': isHeaderSticky,
      }),
    [isHeaderSticky],
  );
  const surfHeadingFormatted = useMemo(
    () => (surfUnits ? `Surf (${getWaveUnits(surfUnits)?.toLowerCase()})` : 'Surf'),
    [surfUnits],
  );

  const columnsToDisplay = useMemo(() => {
    const minSurfWidth = !isLargeMobile ? '75px' : '100px';

    // heading configs
    const consistency = !isHistoricView ? { heading: 'Consistency', style: { flex: 3 } } : null;
    const energy = { heading: 'Wave Energy', style: { flex: 3 } };
    const pressure = isHistoricView ? null : { heading: 'Pressure', style: { flex: 3 } };
    const primarySwell = { heading: 'Primary Swell', style: { flex: 5 } };
    const probability = hideProbability ? null : { heading: 'Probability', style: { flex: 3 } };
    const rating =
      isLandscapeMobile || !ratingsHours?.[0]?.rating
        ? null
        : {
            heading: 'Rating',
            style: { flex: 2, minWidth: '110px' },
          };
    const secondarySwell = { heading: 'Secondary Swell', style: { flex: 10 } };
    const surf = {
      heading: surfHeadingFormatted,
      style: { flex: 2, minWidth: minSurfWidth },
    };
    const swell = { heading: 'Swell', style: { flex: 5 } };
    const weather = isHistoricView ? null : { heading: 'Weather', style: { flex: 3 } };
    const wind = { heading: 'Wind', style: { flex: 4 } };

    // heading groups
    const allHeadings = [
      [
        surf,
        rating,
        primarySwell,
        secondarySwell,
        wind,
        energy,
        consistency,
        weather,
        pressure,
        probability,
      ].filter((item) => Boolean(item)),
    ];

    const lastSlide = isHistoricView
      ? null
      : [energy, consistency, weather, pressure, probability].filter((item) => Boolean(item));

    if (isMobile) {
      return [[surf, swell, wind], [primarySwell, secondarySwell], lastSlide].filter((item) =>
        Boolean(item),
      );
    }

    if (isTablet) {
      return [
        [surf, rating, primarySwell, secondarySwell, wind].filter((item) => Boolean(item)),
        lastSlide,
      ].filter((item) => Boolean(item));
    }

    return allHeadings;
  }, [
    hideProbability,
    isHistoricView,
    isLandscapeMobile,
    isLargeMobile,
    isMobile,
    isTablet,
    ratingsHours,
    surfHeadingFormatted,
  ]);

  const segmentProps = {
    modalType: 'tutorial',
    tabName: 'forecast',
    uiElement: 'probability',
  };

  return (
    <Box className={headerWrapperClasses} data-testid="forecast-table-header-wrapper">
      <Box className={styles.headerTableLeftSpacer} />
      <Box ref={headerRef} className={styles.headerTableInnerWrapper}>
        <TableContainer
          className={classNames({
            [styles.headerTable]: true,
          })}
        >
          <Table>
            <TableHead data-testid="forecast-table-header" className={styles.headerTableInner}>
              <TableRow className={styles.headerTableRow}>
                {columnsToDisplay[pageIndex]?.map((column) => {
                  if (column && column.heading === 'Rating') {
                    return null;
                  }
                  if (column) {
                    return (
                      <TableCell
                        key={`header_${column.heading}`}
                        className={styles.headerTablecell}
                        style={column.style}
                      >
                        <Stack alignContent="center" direction="row" spacing={0}>
                          <Typography variant="callout2">{column.heading}</Typography>
                          {column.heading === 'Probability' && (
                            <Information
                              articleId="7G8eJVFNBjIMKdit2VvEQ"
                              segmentProps={segmentProps}
                              tooltip="Probability"
                              tutorialId="5AmLTiYhctYRWm8RojslYU"
                            />
                          )}
                        </Stack>
                      </TableCell>
                    );
                  }
                  return null;
                })}
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default TableHeader;
